import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useMemo } from 'react';
import { api } from '../services/api';
import type { Notification } from '../types';

const POLL_MS = 15_000;

export interface ChatNotificationItem {
  id: string;
  ticketId: string;
  senderName: string;
  messagePreview: string;
  ticketTitle: string;
  createdAt: string;
}

function toChatItem(n: Notification): ChatNotificationItem | null {
  if (n.event_type !== 'comment_added' || !n.metadata?.sender_name || !n.ticket_id) return null;
  return {
    id: n.id,
    ticketId: n.ticket_id,
    senderName: n.metadata.sender_name,
    messagePreview: n.metadata.message_preview || '',
    ticketTitle: n.metadata.ticket_title || n.ticket_title || '',
    createdAt: n.created_at,
  };
}

/** Group by ticket — keep latest message per conversation */
function groupChatNotifications(items: ChatNotificationItem[]): ChatNotificationItem[] {
  const byTicket = new Map<string, ChatNotificationItem>();
  for (const item of items) {
    const existing = byTicket.get(item.ticketId);
    if (!existing || new Date(item.createdAt) > new Date(existing.createdAt)) {
      byTicket.set(item.ticketId, item);
    }
  }
  return [...byTicket.values()].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
}

export function useLiveChatNotifications() {
  const queryClient = useQueryClient();
  const knownIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const { data, isSuccess } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => api.getNotifications(),
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.data?.type === 'ote-push') {
        queryClient.invalidateQueries({ queryKey: ['notifications'] });
      }
    };
    navigator.serviceWorker?.addEventListener('message', onMessage);
    return () => navigator.serviceWorker?.removeEventListener('message', onMessage);
  }, [queryClient]);

  const notifications: Notification[] = useMemo(() => data?.data ?? [], [data]);

  const chatItems = useMemo(() => {
    const items = notifications
      .map(toChatItem)
      .filter((x): x is ChatNotificationItem => x !== null);
    return groupChatNotifications(items);
  }, [notifications]);

  const newArrivals = useMemo(() => {
    if (!isSuccess) return [];

    const arrivals: ChatNotificationItem[] = [];
    for (const n of notifications) {
      const item = toChatItem(n);
      if (!item) continue;
      if (!knownIdsRef.current.has(n.id)) {
        if (initializedRef.current) {
          arrivals.push(item);
        }
        knownIdsRef.current.add(n.id);
      }
    }

    if (!initializedRef.current && notifications.length >= 0) {
      initializedRef.current = true;
    }

    return arrivals;
  }, [notifications, isSuccess]);

  return {
    chatItems,
    newArrivals,
    totalUnread: chatItems.length,
    pollIntervalMs: POLL_MS,
  };
}

export function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

const AVATAR_COLORS = [
  'bg-primary-600',
  'bg-success-600',
  'bg-warning-600',
  'bg-danger-500',
  'bg-primary-600',
  'bg-primary-600',
];

export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
